import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { omnimizerApi } from "../api";
import { gpuConflict, switchHolders, releaseHolders } from "../lifecycle";
import type { GPUCapacityConflict } from "../types";

function delay(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const cancel = () => { window.clearTimeout(timer); reject(new DOMException("Cancelled", "AbortError")); };
    const timer = window.setTimeout(() => { signal.removeEventListener("abort", cancel); resolve(); }, 1500);
    if (signal.aborted) cancel(); else signal.addEventListener("abort", cancel, { once: true });
  });
}

async function waitUntilStopped(id: string, signal: AbortSignal) {
  const deadline = Date.now() + 15 * 60 * 1000;
  while (!signal.aborted && Date.now() < deadline) {
    const rows = await omnimizerApi.listDeployments();
    if (signal.aborted) return false;
    const holder = rows.find((row) => row.model_id === id);
    if (holder && holder.status === "PAUSED") return true;
    if (!holder || holder.status === "FAILED") throw new Error("Holder stop was not confirmed");
    await delay(signal);
  }
  if (signal.aborted) return false;
  throw new Error("Timed out waiting for the GPU to be released");
}

function GpuConflictDialog({ conflict, finish }: { conflict: GPUCapacityConflict; finish: (proceed: boolean) => void }) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState("");
  const [failed, setFailed] = useState(false);
  const controller = useRef<AbortController>();
  useEffect(() => () => controller.current?.abort(), []);
  const holders = switchHolders({ ...conflict, holders: conflict.holders.map(h => ({ ...h, status: h.status || "STARTED", current_step: h.current_step || null, sleep_capable: !!h.sleep_capable })) });
  const cancel = () => { controller.current?.abort(); finish(false); };
  async function switchModels() {
    const current = new AbortController(); controller.current = current;
    setFailed(false);
    try {
      for (const holder of holders) {
        if (current.signal.aborted) return;
        setProgress(t("omnimizer.lifecycle.waitingForStop", { name: holder.name }));
        await releaseHolders([{ ...holder, status: holder.status || "STARTED", current_step: holder.current_step || null, sleep_capable: !!holder.sleep_capable }]);
        if (!await waitUntilStopped(holder.model_id, current.signal)) return;
      }
      if (!current.signal.aborted) finish(true);
    } catch {
      if (!current.signal.aborted) { setFailed(true); setProgress(""); }
    }
  }
  return <Dialog open onOpenChange={(open) => { if (!open) cancel(); }}><DialogContent>
    <DialogHeader><DialogTitle>{t("omnimizer.lifecycle.gpuBusy")}</DialogTitle>
      <DialogDescription>{t("omnimizer.lifecycle.switchPrompt", { holders: holders.map((h) => h.name).join(", ") })}</DialogDescription>
    </DialogHeader>
    <p className="text-sm text-muted-foreground">{t("omnimizer.lifecycle.stopWarning")}</p>
    {progress && <p role="status">{progress}</p>}
    {failed && <p role="alert" className="text-destructive">{t("omnimizer.lifecycle.switchFailed")}</p>}
    <DialogFooter><Button variant="outline" onClick={cancel}>{t("omnimizer.lifecycle.cancel")}</Button>
      <Button disabled={Boolean(progress) || !holders.length || conflict.code === "gpu_held_by_others"} onClick={() => void switchModels()}>{t("omnimizer.lifecycle.switch")}</Button>
    </DialogFooter>
  </DialogContent></Dialog>;
}

export function useGpuConflictDialog() {
  const [pending, setPending] = useState<{ conflict: GPUCapacityConflict; resolve: (value: boolean) => void }>();
  const resolver = useRef<((value: boolean) => void)>();
  useEffect(() => () => resolver.current?.(false), []);
  function resolveConflict(error: unknown) {
    const conflict = gpuConflict(error);
    if (!conflict) return undefined;
    return new Promise<boolean>((resolve) => {
      resolver.current?.(false); resolver.current = resolve;
      setPending({ conflict, resolve });
    });
  }
  const dialog = pending && <GpuConflictDialog conflict={pending.conflict} finish={(proceed) => {
    pending.resolve(proceed); resolver.current = undefined; setPending(undefined);
  }} />;
  return { resolveConflict, dialog };
}
